import React, { createContext, useContext, useState, useRef, useEffect } from "react";
import ReactDOM from "react-dom";
import styles from "../styles/components/ConfirmDialog.module.css";

const ConfirmContext = createContext(null);

export function ConfirmDialogProvider({ children }) {
    const [state, setState] = useState({
        open: false,
        options: {},
        resolver: null,
    });

    function confirm(options) {
        return new Promise((resolve) => {
            setState({ open: true, options, resolver: resolve });
        });
    }

    function handleClose(result) {
        if (state.resolver) state.resolver(result);
        setState({ open: false, options: {}, resolver: null });
    }

    return (
        <ConfirmContext.Provider value={confirm}>
            {children}
            {state.open && (
                <ConfirmDialog
                    {...state.options}
                    onConfirm={() => handleClose(true)}
                    onCancel={() => handleClose(false)}
                />
            )}
        </ConfirmContext.Provider>
    );
}

export function useConfirm() {
    const ctx = useContext(ConfirmContext);
    if (!ctx) throw new Error("useConfirm must be used within a ConfirmDialogProvider");
    return ctx;
}

function ConfirmDialog({ title, body, confirmText = "OK", cancelText = "Cancel", tone = "primary", onConfirm, onCancel }) {
    const dialogRef = useRef();

    // Focus trap
    useEffect(() => {
        const first = dialogRef.current?.querySelector("button");
        first?.focus();
    }, []);

    useEffect(() => {
        function onKey(e) {
            if (e.key === "Escape") onCancel();
            if (e.key === "Enter") onConfirm();
        }
        window.addEventListener("keydown", onKey);
        return () => window.removeEventListener("keydown", onKey);
    }, [onCancel, onConfirm]);

    return ReactDOM.createPortal(
        <div className={styles.backdrop} onClick={onCancel}>
            <div
                className={styles.dialog}
                role="dialog"
                aria-modal="true"
                aria-labelledby="confirm-title"
                aria-describedby="confirm-body"
                ref={dialogRef}
                onClick={(e) => e.stopPropagation()}
            >
                <h2 id="confirm-title" className={styles.title}>{title}</h2>
                <div id="confirm-body" className={styles.body}>{body}</div>
                <div className={styles.footer}>
                    <button className={styles.cancelButton} onClick={onCancel}>{cancelText}</button>
                    <button
                        className={`${styles.confirmButton} ${tone === "destructive" ? styles.destructive : ""}`}
                        onClick={onConfirm}
                    >
                        {confirmText}
                    </button>
                </div>
            </div>
        </div>,
        document.body
    );
}
